import {IColumnMetadata} from "@/features/application/api/applicationModels";
import React, {FC, useEffect, useState} from "react";
import {Button, Checkbox, Icon, Popup} from "semantic-ui-react";
import dataFormatUtil from "@/util/dataFormatUtil";
import {IDataTableFilter} from "./DataTableModels";

interface IDataTableHeaderFilterBoolean {
    //filters can also be set from outside the column filter itself, like clicking on an employee
    //in another component and then seeing just that employee in the grid.
    //The "applied" filters are controlled by a parent component.  When this component needs to update
    //the applied filters it does it via the parent by calling setFilters which is passed in as a prop here
    appliedFilters?: IDataTableFilter;
    column: IColumnMetadata;
    setFilters: (
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ) => void;
}

const DataTableHeaderFilterBoolean: FC<IDataTableHeaderFilterBoolean> = ({
    appliedFilters,
    column,
    setFilters
}) => {
    //selected, but not necessarily applied filters
    const [selectedFilters, setSelectedFilters] = useState<IDataTableFilter>(
        new Set()
    );

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    //const filterOptions: ITextFilterOptions;

    useEffect(() => {
        if (appliedFilters) {
            const initialFilters: IDataTableFilter = new Set(appliedFilters);
            setSelectedFilters(initialFilters);
        } else {
            setSelectedFilters(new Set());
        }
    }, [appliedFilters]);

    const displayFilterOptions = (column: IColumnMetadata) => {
        const filterContent: JSX.Element[] = [];

        const filterChoices = [true, false];

        filterChoices.forEach((filterValue, index) => {
            const value = dataFormatUtil.dataTableDisplayValue(filterValue, column);

            filterContent.push(
                <React.Fragment key={index}>
                    <Checkbox
                        key={value}
                        name={value}
                        label={value}
                        style={{marginBottom: "1em"}}
                        checked={selectedFilters.has(filterValue)}
                        onClick={() => {
                            toggleSelection(filterValue);
                        }}
                    />
                    <br />
                </React.Fragment>
            );
        });

        return filterContent;
    };

    const toggleSelection = (filterValue: boolean) => {
        if (selectedFilters.has(filterValue)) {
            //extra work to create a new Set object with the value removed for the state value
            //candidate for using immutable.js
            const newFilters = new Set(selectedFilters);
            newFilters.delete(filterValue);

            setSelectedFilters(newFilters);
        } else {
            const newFilters = new Set(selectedFilters);
            newFilters.add(filterValue);

            setSelectedFilters(newFilters);
        }
    };

    return (
        <Popup
            key={column.key}
            trigger={
                <Icon
                    name="filter"
                    circular={true}
                    className="filterIcon"
                    size="small"
                    inverted={appliedFilters ? true : false}
                ></Icon>
            }
            open={isFilterOpen}
            onOpen={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                // Stop propagation on onOpen stops propagation (sorting the column) when the popup icon is clicked
                event.stopPropagation();
                setIsFilterOpen(true);
            }}
            onClose={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                // Stop propagation on onClose stops propagation (sorting the column) when the popup icon is clicked
                event.stopPropagation();
                setIsFilterOpen(false);
                // Reset selected filter to the applied filters
                setSelectedFilters(new Set(appliedFilters));
            }}
            onClick={(e: any) => {
                // Stop propagation on onClick stops propagation (sorting the column) when the popup body is clicked
                e.stopPropagation();
            }}
            position={"top center"}
            openOnTriggerMouseEnter={false}
            openOnTriggerClick={true}
            closeOnTriggerClick={true}
            closeOnDocumentClick={true}
            closeOnEscape={true}
            closeOnTriggerMouseLeave={false}
            style={{maxHeight: "400px"}}
        >
            Filter by:
            <br />
            <br />
            <div style={{maxHeight: "300px", overflow: "auto"}}>
                {displayFilterOptions(column)}
            </div>
            <Button
                className="btn btn-blue-solid"
                onClick={() => {
                    setFilters(column.key, selectedFilters);
                    setIsFilterOpen(false);
                }}
                style={{marginTop: "5px"}}
            >
                Apply
            </Button>
            <Button
                className="btn btn-blue"
                onClick={() => {
                    setIsFilterOpen(false);
                    // Reset selected filter to the applied filters
                    setSelectedFilters(new Set(appliedFilters));
                }}
            >
                Cancel
            </Button>
        </Popup>
    );
};

export default DataTableHeaderFilterBoolean;
